import { Check, Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/cn";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { ErrorState, Skeleton } from "@/components/ui/states";
import { friendlyError } from "@/lib/errors";
import { useFeatures, useModuleAccess, usePlan } from "./hooks";

/**
 * Lo que la tienda tiene y lo que no.
 *
 * Es la unica pantalla del panel escrita para que alguien pida algo. Aun asi
 * no exagera: cada linea bloqueada dice que hace, no que se esta perdiendo.
 */
export function PlanPage() {
  const plan = usePlan();
  const modulos = useModuleAccess();
  const features = useFeatures();

  if (plan.isPending || modulos.isPending || features.isPending) {
    return <Skeleton className="h-96 w-full rounded-lg" />;
  }
  if (plan.isError) {
    return (
      <ErrorState message={friendlyError(plan.error)} onRetry={() => void plan.refetch()} />
    );
  }

  const lista = modulos.data ?? [];
  const activos = lista.filter((m) => m.contratado);
  const bloqueados = lista.filter((m) => !m.contratado);

  const capacidades = features.data ?? [];
  const incluidas = capacidades.filter((f) => f.incluida);
  const fuera = capacidades.filter((f) => !f.incluida);

  return (
    <div className="flex flex-col gap-3">
      <Panel title="Tu plan">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-md bg-accent-soft px-3 py-1.5 text-emph font-semibold text-accent">
            {plan.data?.name ?? "—"}
          </span>
          <p className="text-ui text-ink-2">
            {plan.data?.code === "web"
              ? "Tienda web: recibes y despachas lo que entra por internet."
              : "Incluye las secciones y funciones marcadas abajo."}
          </p>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <Dato etiqueta="Secciones activas" valor={`${activos.length} de ${lista.length}`} />
          <Dato
            etiqueta="Atributos de producto"
            valor={plan.data?.maxAttributes === null ? "Sin límite" : String(plan.data?.maxAttributes ?? "—")}
          />
          <Dato
            etiqueta="Funciones incluidas"
            valor={`${incluidas.length} de ${capacidades.length}`}
          />
        </div>
      </Panel>

      <Panel title="Secciones">
        <div className="grid gap-1.5 sm:grid-cols-2">
          {lista.map((m) => (
            <div
              key={m.key}
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-2 text-ui",
                m.contratado ? "border-line bg-surface" : "border-line-2 bg-sunk text-ink-3",
              )}
            >
              {m.contratado ? (
                <Check className="size-3.5 shrink-0 text-ok" />
              ) : (
                <Lock className="size-3.5 shrink-0 text-ink-3" />
              )}
              <span className={cn("truncate", m.contratado && "font-medium")}>{m.name}</span>
              {!m.contratado && (
                <StatusPill tone="neutral" className="ml-auto">
                  {m.tier === "pro" ? "Pro" : "Otro plan"}
                </StatusPill>
              )}
            </div>
          ))}
        </div>
      </Panel>

      {fuera.length > 0 && (
        <Panel
          title={
            <span className="flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-accent" />
              Lo que suma pagar mensualidad
            </span>
          }
        >
          <ul className="flex flex-col gap-2.5">
            {fuera.map((f) => (
              <li key={f.key} className="flex gap-2.5">
                <Lock className="mt-0.5 size-3.5 shrink-0 text-ink-3" />
                <span>
                  <b className="text-ui">{f.name}</b>
                  {f.description && (
                    <span className="mt-0.5 block text-meta text-ink-2">{f.description}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-4 border-t border-line-2 pt-3 text-meta text-ink-3">
            El cambio de plan lo hacemos nosotros: escríbenos y lo activamos sin que
            pierdas nada de lo que ya tienes cargado.
          </p>
        </Panel>
      )}

      {bloqueados.length === 0 && fuera.length === 0 && (
        <Panel>
          <p className="px-1 py-6 text-center text-ui text-ink-2">
            Tu plan incluye todo lo que Synkro ofrece hoy.
          </p>
        </Panel>
      )}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="rounded-md border border-line bg-sunk px-3 py-2.5">
      <span className="block font-mono text-mark uppercase tracking-[0.1em] text-ink-3">
        {etiqueta}
      </span>
      <span className="mt-0.5 block text-emph font-semibold">{valor}</span>
    </div>
  );
}
